import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { Close, CloudUpload, Delete, CheckCircle } from '@mui/icons-material';

interface CommonLayerUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  certificate?: any;
  uploadPdfMutation: (file: File) => Promise<{
    success: boolean;
    url?: string;
    error?: string;
  }>;
  uploadCertificateMutation: (input: {
    url: string;
    kyc_id: string;
    document_type: string;
  }) => Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }>;
  kycId: string;
}

const CommonLayerUpload: React.FC<CommonLayerUploadProps> = ({
  open,
  onClose,
  onUpload,
  certificate,
  uploadPdfMutation,
  uploadCertificateMutation,
  kycId
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setUploadSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      setIsUploading(true);
      try {
        // Step 1: Upload PDF file to get the URL
        const pdfUploadResult = await uploadPdfMutation(selectedFile);
        
        if (!pdfUploadResult.success || !pdfUploadResult.url) {
          throw new Error(pdfUploadResult.error || 'Failed to upload PDF');
        }

        // Step 2: Upload certificate using the obtained URL
        const certificateUploadResult = await uploadCertificateMutation({
          url: pdfUploadResult.url,
          kyc_id: kycId,
          document_type: 'common_layer_cert'
        });

        if (!certificateUploadResult.success) {
          throw new Error(certificateUploadResult.error || 'Failed to upload certificate');
        }

        setUploadSuccess(true);
        setSnackbarMessage('Common Layer Certificate uploaded successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Call the original onUpload callback if needed
        onUpload(selectedFile);

        // Close dialog after successful upload
        setTimeout(() => {
          setSelectedFile(null);
          setIsUploading(false);
          onClose();
        }, 1500);
      } catch (error: any) {
        setSnackbarMessage(error.message || 'Error uploading certificate. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsUploading(false);
      }
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setUploadSuccess(false);
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle sx={{ position: 'relative', pr: 5, fontWeight: 'bold' }}>
          Upload Certificate
          <IconButton
            onClick={handleCancel}
            size="small"
            sx={{ color: 'inherit', position: 'absolute', right: 8, top: 8 }}
            disabled={isUploading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upload Common Layer Certificate
              <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ccc',
                borderRadius: 2,
                px: 2,
                py: 1,
                background: '#fff',
                minHeight: 56,
              }}
            >
              <input
                id="common-layer-file-upload"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <label htmlFor="common-layer-file-upload">
                <Button
                  variant="contained"
                  component="span"
                  sx={{
                    background: '#000',
                    color: '#fff',
                    textTransform: 'none',
                    mr: 2,
                    minWidth: 120,
                    '&:hover': { background: '#333' }
                  }}
                  disabled={isUploading}
                >
                  Choose file
                </Button>
              </label>
              {selectedFile ? (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#222', fontSize: 16 }}>
                      {selectedFile.name}
                    </Typography>
                    {uploadSuccess && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <CheckCircle fontSize="small" sx={{ color: 'green' }} />
                        <Typography variant="body2" color="green">
                          File uploaded successfully
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    onClick={() => {
                      setSelectedFile(null);
                      setUploadSuccess(false);
                    }}
                    sx={{ color: '#E60012' }}
                    title="Remove file"
                    disabled={isUploading}
                  >
                    <Delete />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography sx={{ flex: 1, color: '#888', fontSize: 16 }}>
                    No file chosen
                  </Typography>
                  <IconButton disabled>
                    <CloudUpload sx={{ color: '#888' }} />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
          {isUploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={handleCancel} disabled={isUploading}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || isUploading}
            sx={{
              backgroundColor: '#E60012',
              '&:hover': { backgroundColor: '#cc0010' },
            }}
          >
            Upload File
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CommonLayerUpload;